import { useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { PriceSimulationCompensationTypeDto } from '@eneco/dc-be/client';
import { Button, Heading, InputSelect, Stack, Stretch, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { useFormContext } from 'react-hook-form';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const BiDirectionalMeterStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { trackEventBE } = useTracking();

  const {
    register,
    formState: { errors },
    watch,
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();

  const onNextClick = () => {
    trackEventBE('pbt_biDirectionalMeter', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  const compensationType = watch('compensationType');

  const filteredCompensationTypeList = useMemo(() => {
    const compensationOptionsList = fields.biDirectionalMeterStep.compensationOptionsList.value.options;
    return compensationOptionsList.filter(x => {
      const val = x.name as PriceSimulationCompensationTypeDto;
      return val === 'Compensation' || val === 'Valorisation';
    });
  }, [fields.biDirectionalMeterStep.compensationOptionsList.value.options]);

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.biDirectionalMeterStep.title.value}
        </Heading>
        <Text>{fields.biDirectionalMeterStep.subTitle.value}</Text>
      </Stack>
      <Stretch>
        <Stack gap={10} direction={'column'}>
          <InputSelect
            placeholder=""
            label={fields.biDirectionalMeterStep.compensationOptionsList.value.label}
            hint={<RichText html={fields.biDirectionalMeterStep.compensationOptionsList.value.hint} />}
            error={errors.compensationType?.message}
            options={filteredCompensationTypeList.map(x => ({ label: x.label, value: x.name })) ?? []}
            defaultValue={compensationType}
            isOptional={false}
            {...register('compensationType')}
          />
          <label htmlFor={'compensationType'}>
            <Stack>
              <Text weight="bold">{fields.biDirectionalMeterStep.explanationTitle.value}</Text>
              <RichText html={fields.biDirectionalMeterStep.explanationDescription.value} />
            </Stack>
          </label>
        </Stack>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
